import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes.ts';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconCheck } from '@sorare/core/src/atoms/icons/system/iconCheck.ts';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL, BrandXL } from '@sorare/core/src/atoms/typography';
import { FrameCorners } from '@sorare/core/src/atoms/ui/FrameCorners';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { SetBrandingImage } from '@sorare/core/src/set/branding/SetBrandingImage.tsx';
import { useCardSetBranding } from '@sorare/core/src/set/branding/useCardSetBranding.ts';

import { MissionSection } from '#src/components/missions/MissionSection/index.tsx';
import { PlayMissionsTaskRow } from '#src/components/missions/PlayTask/index.tsx';
import { TaskClaimedDialogs } from '#src/components/missions/TaskClaimedDialogs/index.tsx';
import { useTaskClaimedDialogs } from '#src/components/missions/TaskClaimedDialogs/useTaskClaimedDialogs.tsx';

import type { PlayMissions_currentUser } from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 3 })`
  width: 100%;
`;

type Props = {
  currentUser: Nullable<PlayMissions_currentUser>;
  refetch?: () => void;
};

export const PlayMissions = ({ currentUser, refetch }: Props) => {
  const setPlayTasks = currentUser?.setPlayTasks;
  const { setIconUrl } = useCardSetBranding();

  const {
    onClaim,
    loadingTaskId,
    pickerDialogTask,
    setPickerDialogTask,
    revealPackCardsDialogReward,
    setRevealPackCardsDialogReward,
    rewardDialogTask,
    setRewardDialogTask,
  } = useTaskClaimedDialogs();

  const general = setPlayTasks?.filter(
    task => task.aasmState !== ManagerProgressionTaskState.CLAIMED
  );
  const completed = setPlayTasks?.filter(
    task => task.aasmState === ManagerProgressionTaskState.CLAIMED
  );

  const renderRow = (task: NonNullable<typeof setPlayTasks>[number]) => (
    <PlayMissionsTaskRow
      key={task.id}
      task={task}
      onClaim={
        isType(task, 'Task')
          ? () => {
              onClaim(task);
              refetch?.();
            }
          : undefined
      }
      loading={loadingTaskId === task.id}
    />
  );

  return (
    <>
      <TaskClaimedDialogs
        pickerDialogTask={pickerDialogTask}
        onPickerDialogClose={() => {
          setPickerDialogTask(null);
          refetch?.();
        }}
        revealPackCardsDialogReward={revealPackCardsDialogReward}
        onRevealPackCardsDialogClose={() => {
          setRevealPackCardsDialogReward(null);
          refetch?.();
        }}
        rewardDialogTask={rewardDialogTask}
        onRewardDialogClose={() => {
          setRewardDialogTask(null);
          refetch?.();
        }}
      />
      <Vertical gap={5}>
        <Vertical center gap={2}>
          <div className="relative px-2 py-1">
            <FrameCorners cornerSize={8} color="var(--c-black)" />
            <BrandXL as="span" color="var(--c-black)">
              <FormattedMessage {...glossary.play} />
            </BrandXL>
          </div>
          <BodyL className="text-center" color="var(--c-black)">
            <FormattedMessage
              defaultMessage="Get rewarded by playing daily"
              description="PlayMissions - subtitle"
            />
          </BodyL>
        </Vertical>
        {(!!general?.length || !!completed?.length) && (
          <Root>
            {!!general?.length && (
              <MissionSection
                icon={<SetBrandingImage url={setIconUrl} size={2} />}
                iconColor="var(--c-white)"
                title={<FormattedMessage defaultMessage="General" />}
              >
                {general.map(renderRow)}
              </MissionSection>
            )}
            {!!completed?.length && (
              <MissionSection
                icon={<FontAwesomeIcon icon={iconCheck} />}
                iconColor="var(--c-white)"
                title={<FormattedMessage defaultMessage="Completed" />}
              >
                {completed.map(renderRow)}
              </MissionSection>
            )}
          </Root>
        )}
      </Vertical>
    </>
  );
};

PlayMissions.fragments = {
  currentUser: gql`
    fragment PlayMissions_currentUser on CurrentUser {
      slug
      setPlayTasks(sport: $sport) {
        id
        __typename
        aasmState
        ...PlayMissionsTaskRow_taskInterface
        ...TaskClaimedDialogs_taskInterface
      }
    }
    ${PlayMissionsTaskRow.fragments.taskInterface}
    ${TaskClaimedDialogs.fragments.taskInterface}
  ` as TypedDocumentNode<PlayMissions_currentUser>,
};
